import React from "react";
import styled from "styled-components";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";

export default function JournalPage({ navigate }) {
  return (
    <>
      <TopNavbar navigate={navigate} currentPage="journal" />

      {/* ===== PAGE HERO ===== */}
      <PageHero className="pageHero">
        <div className="container flexSpaceCenter">
          <HeroText className="hero-in">
            <PageTag className="pinkBg whiteColor radius6">המוצר שלנו</PageTag>
            <h1 className="font60 extraBold darkColor heroTitle" style={{ marginTop: "15px", lineHeight: "1.1" }}>
              היומן הויזואלי 📒
            </h1>
            <p className="font18 darkColor" style={{ marginTop: "20px", lineHeight: "1.7", maxWidth: "500px" }}>
              יומן עם לב פתוח, ולא רק עם עט.
              המרחב האישי שלך לכתוב, לצייר, להרגיש.
            </p>
          </HeroText>
          <HeroImageBox>
            {/* IMAGE PLACEHOLDER — swap for a hero photo of a visual journal */}
            <HeroImgPlaceholder className="radius8">
              <span>📸 תמונת גיבור: יומן ויזואלי</span>
            </HeroImgPlaceholder>
          </HeroImageBox>
        </div>
      </PageHero>

      {/* ===== NOSTALGIC OPENER ===== */}
      <Section className="lightBg">
        <div className="container">
          <TwoCol>
            <ImageCol className="slide-left">
              {/* IMAGE PLACEHOLDER — swap for a nostalgic diary/journal photo */}
              <ImgPlaceholder className="radius8" style={{ background: "var(--tint-mauve)", height: "380px", color: "var(--rose-600)" }}>
                <span>📸 תמונה: יומן ישן / נוסטלגיה</span>
              </ImgPlaceholder>
            </ImageCol>
            <TextCol className="slide-right">
              <SectionLabel className="font15 semiBold" style={{ color: "var(--rose-600)" }}>קצת נוסטלגיה</SectionLabel>
              <h2 className="font40 extraBold darkColor" style={{ margin: "10px 0 20px" }}>
                🖋️ זוכרים את היומן של פעם?
              </h2>
              <BodyText className="font15">
                מי כתבה יומן בשנות הילדות והנעורים?
                יומן סודי כזה, עם מנעול קטן או מדבקות של סנופי ופרחים...
              </BodyText>
              <BodyText className="font15" style={{ marginTop: "16px" }}>
                היינו כותבות בו מילים מילים מילים: סודות, אהבות ראשונות, מריבות עם חברה הכי טובה,
                התלבטויות, רגעים של שמחה ופחד, כל מה שקרה לנו באותו יום.
              </BodyText>
              <BodyText className="font15" style={{ marginTop: "16px" }}>
                אז היום, היומן קם לתחייה, אבל בגרסה חדשה:
              </BodyText>
              <Highlight className="font18 semiBold" style={{ marginTop: "20px" }}>
                📒 היומן הוויזואלי: יומן עם לב פתוח, ולא רק עם עט.
              </Highlight>
            </TextCol>
          </TwoCol>
        </div>
      </Section>

      {/* ===== WHAT IS A VISUAL JOURNAL ===== */}
      <Section>
        <div className="container">
          <TwoCol>
            <TextCol className="slide-right">
              <SectionLabel className="font15 semiBold" style={{ color: "var(--rose-600)" }}>הגדרה</SectionLabel>
              <h2 className="font40 extraBold darkColor" style={{ margin: "10px 0 20px" }}>
                🎨 מה זה יומן ויזואלי בעצם?
              </h2>
              <BodyText className="font15">
                יומן שבו כותבים, אבל גם מציירים, מדביקים, גוזרים, שופכים צבעים, משרבטים,
                וממציאים את השפה האישית שלנו מחדש.
              </BodyText>
              <BodyText className="font15" style={{ marginTop: "16px" }}>
                אין בו חוקים. אין יפה או מכוער.
                אין "כישרון" או "אין לי ידיים טובות".
                זה מרחב אישי שבו הכל הולך, כל מה שיוצא ממך, נכון!
              </BodyText>
              <FeatureList>
                <FeatureItem>✏️ כתיבה חופשית</FeatureItem>
                <FeatureItem>🎨 ציור וצבעים</FeatureItem>
                <FeatureItem>✂️ קולאז׳ והדבקות</FeatureItem>
                <FeatureItem>💭 מחשבות ורגשות</FeatureItem>
              </FeatureList>
            </TextCol>
            <ImageCol className="slide-left">
              <ImgStack>
                {/* IMAGE PLACEHOLDER — main journal open page photo */}
                <ImgPlaceholder className="radius8" style={{ background: "var(--tint-butter)", height: "280px" }}>
                  <span>📸 תמונת יומן פתוח</span>
                </ImgPlaceholder>
                <SmallImgRow>
                  {/* IMAGE PLACEHOLDER — small detail photos */}
                  <SmallImg className="radius6" style={{ background: "var(--tint-rose)" }}>
                    <span>📸 פרט 1</span>
                  </SmallImg>
                  <SmallImg className="radius6" style={{ background: "var(--tint-mauve)" }}>
                    <span>📸 פרט 2</span>
                  </SmallImg>
                </SmallImgRow>
              </ImgStack>
            </ImageCol>
          </TwoCol>
        </div>
      </Section>

      {/* ===== HOW IT LOOKS ===== */}
      <Section className="coralBg">
        <div className="container">
          <SectionHeader>
            <h2 className="font40 extraBold darkColor">✨ איך זה נראה?</h2>
          </SectionHeader>
          <HowItLooksGrid className="stagger-reveal">
            <HowItem>
              {/* IMAGE PLACEHOLDER — simple notebook with pens */}
              <ImgPlaceholder className="radius8" style={{ background: "var(--tint-paper)", height: "220px" }}>
                <span>📸 מחברת עם עטים</span>
              </ImgPlaceholder>
              <HowItemText className="font14">
                זה יכול להיראות כמו מחברת פשוטה עם עטים וצבעים.
              </HowItemText>
            </HowItem>
            <HowItem>
              {/* IMAGE PLACEHOLDER — handmade journal */}
              <ImgPlaceholder className="radius8" style={{ background: "var(--tint-butter)", height: "220px" }}>
                <span>📸 מחברת תפורה ביד</span>
              </ImgPlaceholder>
              <HowItemText className="font14">
                בדרך כלל זו מחברת שתפרנו בעצמנו, או ספר שאנחנו עובדות בו.
              </HowItemText>
            </HowItem>
            <HowItem>
              {/* IMAGE PLACEHOLDER — journal interior with art */}
              <ImgPlaceholder className="radius8" style={{ background: "var(--tint-rose)", height: "220px" }}>
                <span>📸 עמוד פנימי: עולם שלם</span>
              </ImgPlaceholder>
              <HowItemText className="font14">
                ובפנים? עולם שלם של רגשות, מחשבות, תהיות, זיכרונות, חלומות וקסם.
              </HowItemText>
            </HowItem>
          </HowItLooksGrid>
        </div>
      </Section>

      {/* ===== THE HEALING SCIENCE ===== */}
      <Section>
        <div className="container">
          <TwoCol>
            <ImageCol className="slide-left">
              <ImgStack>
                {/* IMAGE PLACEHOLDER — hands working on journal */}
                <ImgPlaceholder className="radius8" style={{ background: "var(--grad-blush)", height: "320px" }}>
                  <span>📸 ידיים על הדף: יצירה</span>
                </ImgPlaceholder>
                {/* IMAGE PLACEHOLDER — relaxed/peaceful expression */}
                <AbsoluteImg className="radius8" style={{ background: "var(--tint-mauve)", color: "var(--rose-600)" }}>
                  <span>📸 הרגעה וניקוי</span>
                </AbsoluteImg>
              </ImgStack>
            </ImageCol>
            <TextCol className="slide-right">
              <SectionLabel className="font15 semiBold" style={{ color: "var(--rose-600)" }}>מה כל כך טוב בזה?</SectionLabel>
              <h2 className="font30 extraBold darkColor" style={{ margin: "10px 0 20px" }}>
                🧠 כשאנחנו יוצרות, הגוף עסוק, והמוח נרגע.
              </h2>
              <BodyText className="font15">
                בזמן שידינו זזות על הדף, משתחררים אנדורפינים, ההורמונים שמרגיעים אותנו.
                פתאום הלחץ נרגע, החרדה שוקעת, המחשבות נהיות קלות יותר.
              </BodyText>
              <BodyText className="font15" style={{ marginTop: "16px" }}>
                היצירה משחררת, מזככת, ומרפאת, גם אם רק שרבטת עיגולים בצבע טורקיז.
              </BodyText>
              <BodyText className="font15" style={{ marginTop: "16px" }}>
                מחקרים מראים שפעולה יצירתית ממוקדת בצבע, תנועה ודימוי
                עוזרת לנו להתנתק מהטרדות היומיומיות ולהתחבר לעצמנו מחדש.
              </BodyText>
              <QuoteBox className="coralBg radius8">
                <p className="font15 semiBold">
                  💬 זה לא רק יצירה. זה ריפוי. זה את.
                </p>
              </QuoteBox>
            </TextCol>
          </TwoCol>
        </div>
      </Section>

      {/* ===== GALLERY ===== */}
      <GallerySection className="greenBg">
        <div className="container">
          <SectionHeader>
            <h2 className="font30 extraBold darkColor">גלריית יומנים ✨</h2>
            <p className="font14 darkColor">השראה מסדנאות שלנו</p>
          </SectionHeader>
          <GalleryGrid className="stagger-reveal">
            {[1,2,3,4,5,6].map((i) => (
              <GalleryItem key={i}>
                {/* IMAGE PLACEHOLDER — swap for real journal pages */}
                <GalleryImg className="radius8" style={{ background: i % 2 === 0 ? "var(--tint-paper)" : "var(--tint-rose)" }}>
                  <span>📸 יומן {i}</span>
                </GalleryImg>
              </GalleryItem>
            ))}
          </GalleryGrid>
        </div>
      </GallerySection>

      {/* ===== CTA ===== */}
      <CTASection className="darkBg">
        <div className="container textCenter">
          <h2 className="font40 extraBold whiteColor">📩 אז מתי את מתחילה יומן משלך?</h2>
          <CTAImageRow>
            {/* IMAGE PLACEHOLDER — inviting workshop photo */}
            <CTAImg className="radius8" style={{ background: "var(--tint-butter)", borderColor: "var(--rose-300)" }}>
              <span style={{ color: "var(--rose-700)" }}>📸 תמונת סדנה</span>
            </CTAImg>
            <CTAImg className="radius8" style={{ background: "var(--tint-rose)", borderColor: "var(--rose-300)" }}>
              <span style={{ color: "var(--rose-700)" }}>📸 תמונת יצירה</span>
            </CTAImg>
          </CTAImageRow>
          <BackBtn onClick={() => navigate("home")} className="pinkBg whiteColor radius8 pointer semiBold font15">
            חזרה לדף הבית
          </BackBtn>
        </div>
      </CTASection>

      <Footer navigate={navigate} />
    </>
  );
}

/* ---- Styled Components ---- */
const PageHero = styled.section`
  padding: 120px 0 80px;
  direction: rtl;
  @media (max-width: 860px) {
    padding: 100px 0 60px;
    .flexSpaceCenter { flex-direction: column; gap: 40px; }
  }
`;
const HeroText = styled.div`
  width: 55%;
  @media (max-width: 860px) { width: 100%; }
`;
const PageTag = styled.span`
  display: inline-block;
  padding: 5px 14px;
  font-size: 0.813rem;
  font-weight: 600;
`;
const HeroImageBox = styled.div`
  width: 40%;
  @media (max-width: 860px) { width: 100%; }
`;
const HeroImgPlaceholder = styled.div`
  width: 100%;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  background: var(--grad-blush);
  color: var(--rose-600);
  border: 8px solid rgba(255, 255, 255, 0.7);
  box-shadow: var(--shadow-lift);
  animation: float 7s ease-in-out infinite;
`;
const Section = styled.section`
  padding: 80px 0;
  direction: rtl;
`;
const SectionLabel = styled.p``;
const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  p { margin-top: 12px; }
`;
const TwoCol = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  @media (max-width: 860px) { flex-direction: column; }
`;
const TextCol = styled.div`
  flex: 1;
`;
const ImageCol = styled.div`
  flex: 1;
`;
const ImgPlaceholder = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.938rem;
  font-weight: 600;
  color: var(--ink);
`;
const BodyText = styled.p`
  line-height: 1.8;
  color: var(--ink-soft);
`;
const Highlight = styled.p`
  color: var(--rose-600);
  line-height: 1.7;
`;
const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 25px;
`;
const FeatureItem = styled.span`
  background: var(--tint-rose);
  padding: 8px 16px;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--ink);
`;
const ImgStack = styled.div`
  position: relative;
`;
const SmallImgRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;
const SmallImg = styled.div`
  flex: 1;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ink);
`;
const AbsoluteImg = styled.div`
  margin-top: 12px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
`;
const QuoteBox = styled.div`
  margin-top: 25px;
  padding: 20px 25px;
`;
const HowItLooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const HowItem = styled.div``;
const HowItemText = styled.p`
  margin-top: 15px;
  line-height: 1.6;
  color: var(--ink);
  text-align: center;
`;
const GallerySection = styled.section`
  padding: 80px 0;
  direction: rtl;
`;
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;
const GalleryItem = styled.div``;
const GalleryImg = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--ink);
`;
const CTASection = styled.section`
  padding: 80px 0;
  direction: rtl;
`;
const CTAImageRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin: 30px 0;
  @media (max-width: 600px) { flex-direction: column; align-items: center; }
`;
const CTAImg = styled.div`
  width: 280px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px dashed;
  border-radius: 1rem;
`;
const BackBtn = styled.button`
  padding: 14px 30px;
  font-size: 1rem;
  border: none;
  cursor: pointer;
`;
